import { cn } from "@heroui/react";
import { ErrorView } from "src/general/errors/ErrorView";
import type { SlotsToClasses } from "src/utils/componentUtils";
import { PageBreadcrumbs } from "./PageBreadcrumbs";

export interface PageWrapperProps {
  children: React.ReactNode;
  /** Whether the page should take the full width of the container */
  fullWidth?: boolean;
  /** Whether the page should have height fixed to viewport height (like a chat view) */
  fixedHeight?: boolean;
  /** If an error is provided, it will be displayed instead of the main content */
  error?: React.ReactNode;
  className?: string;
  classNames?: SlotsToClasses<"base" | "main">;
  ref?: React.Ref<HTMLDivElement>;
}

/** A layout wrapper component for main content that provides consistent spacing and optional breadcrumb navigation. */
export const PageWrapper = ({
  children,
  fullWidth = false,
  fixedHeight = false,
  error,
  className,
  classNames,
  ref,
}: PageWrapperProps) => {
  return (
    <div
      ref={ref}
      className={cn(
        "@container flex flex-col gap-2 w-full sm:gap-4 p-4 sm:p-6 lg:p-8",
        fixedHeight
          ? "h-[calc(100vh-(calc(16*var(--spacing))))] md:h-screen"
          : "",
        className,
        classNames?.base,
      )}
    >
      {/* Rendered without a condition: a page whose route carries crumb
          metadata needs no configuration, and PageBreadcrumbs draws nothing
          without it. */}
      <PageBreadcrumbs />
      {error ? (
        <ErrorView message={error} />
      ) : (
        <main
          id="main-content"
          className={cn(
            `flex flex-col gap-2 sm:gap-4 w-full`,
            fullWidth ? "max-w-full" : "max-w-md",
            fixedHeight
              ? "h-full &>*:flex-shrink &>*:flex-grow-0 &>*:min-h-0"
              : "",
            classNames?.main,
          )}
        >
          {children}
        </main>
      )}
    </div>
  );
};
